import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import UazapiAPI from '../../api/uazapi';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  currentInstance: null,
  qrcode: null,
  instanceStatuses: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
    isFetchingStatus: false,
  },
};

const DOWN_STATUSES = ['disconnected', 'error'];

export const getters = {
  getInstances($state) {
    return $state.records;
  },
  getCurrentInstance($state) {
    return $state.currentInstance;
  },
  getQrcode($state) {
    return $state.qrcode;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getDownInstances($state) {
    return Object.values($state.instanceStatuses).filter(inst =>
      DOWN_STATUSES.includes(inst.status)
    );
  },
};

export const actions = {
  fetchInstances: async ({ commit }) => {
    commit(types.SET_UAZAPI_UI_FLAG, { isFetching: true });
    try {
      const { data } = await UazapiAPI.get();
      commit(types.SET_UAZAPI_INSTANCES, data);
      data.forEach(instance =>
        commit(types.SET_UAZAPI_INSTANCE_STATUS, instance)
      );
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_UAZAPI_UI_FLAG, { isFetching: false });
    }
  },

  createInstance: async ({ commit }, instanceData) => {
    commit(types.SET_UAZAPI_UI_FLAG, { isCreating: true });
    try {
      const { data } = await UazapiAPI.create(instanceData);
      commit(types.ADD_UAZAPI_INSTANCE, data.instance);
      commit(types.SET_UAZAPI_CURRENT, data.instance);
      commit(types.SET_UAZAPI_QRCODE, data.qrcode);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_UAZAPI_UI_FLAG, { isCreating: false });
    }
    return null;
  },

  fetchStatus: async ({ commit }, id) => {
    commit(types.SET_UAZAPI_UI_FLAG, { isFetchingStatus: true });
    try {
      const { data } = await UazapiAPI.fetchStatus(id);
      commit(types.SET_UAZAPI_CURRENT, data);
      commit(types.EDIT_UAZAPI_INSTANCE, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_UAZAPI_UI_FLAG, { isFetchingStatus: false });
    }
  },

  reconnect: async ({ commit }, id) => {
    try {
      const { data } = await UazapiAPI.reconnect(id);
      commit(types.SET_UAZAPI_CURRENT, data.instance);
      commit(types.SET_UAZAPI_QRCODE, data.qrcode);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  deleteInstance: async ({ commit }, id) => {
    commit(types.SET_UAZAPI_UI_FLAG, { isDeleting: true });
    try {
      await UazapiAPI.delete(id);
      commit(types.DELETE_UAZAPI_INSTANCE, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_UAZAPI_UI_FLAG, { isDeleting: false });
    }
  },

  clearCurrent: ({ commit }) => {
    commit(types.SET_UAZAPI_CURRENT, null);
    commit(types.SET_UAZAPI_QRCODE, null);
  },

  updateInstanceStatus: ({ commit }, data) => {
    commit(types.SET_UAZAPI_INSTANCE_STATUS, data);
  },
};

export const mutations = {
  [types.SET_UAZAPI_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_UAZAPI_INSTANCES]: MutationHelpers.set,
  [types.ADD_UAZAPI_INSTANCE]: MutationHelpers.setSingleRecord,
  [types.EDIT_UAZAPI_INSTANCE]: MutationHelpers.update,
  [types.DELETE_UAZAPI_INSTANCE]: MutationHelpers.destroy,
  [types.SET_UAZAPI_CURRENT]($state, instance) {
    $state.currentInstance = instance;
  },
  [types.SET_UAZAPI_QRCODE]($state, qrcode) {
    $state.qrcode = qrcode;
  },
  [types.SET_UAZAPI_INSTANCE_STATUS]($state, { id, name, status, account_id }) {
    $state.instanceStatuses = {
      ...$state.instanceStatuses,
      [id]: { id, name, status, account_id },
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
